import { MacBrand, MacText } from '@/components/MacBrand';
import { useEffect, useState, useCallback } from "react";
import {
  adminLogin, getToken, clearToken,
  govPayrollBoundaries, govListInsurance, govCreateInsurance, govListRisk, govCreateRisk,
  govListDR, govCreateDR, govListStewardship, govCreateStewardship, govMeshHealth,
} from "@/lib/api";

const inputCls = "w-full bg-transparent border border-[rgba(180,139,63,0.35)] px-3 py-2 text-[var(--bone)] outline-none focus:border-[var(--gold)] mb-2 text-sm";
const hz = (s) => (s == null ? "" : String(s).replace(/JARVIS/gi, "MAC").replace(/_/g, " "));
const errMsg = (d, f) => {
  if (typeof d === "string") return d;
  if (Array.isArray(d)) {
    const e0 = d[0] || {};
    const field = Array.isArray(e0.loc) ? e0.loc[e0.loc.length - 1] : null;
    return `${field ? hz(String(field)) + ": " : ""}${e0.msg || "Invalid input"}`;
  }
  return d?.message || d?.detail || f || "Something went wrong.";
};

const STATUS_TONE = {
  ACTIVE_VERIFIED: "text-[#8fd19e] border-[#8fd19e]/40", VERIFIED: "text-[#8fd19e] border-[#8fd19e]/40",
  PROVEN_CURRENT_LIVE: "text-[#8fd19e] border-[#8fd19e]/40",
  RECORDED_BUT_UNVERIFIED: "text-[#e7a877] border-[#e7a877]/40", UNVERIFIED: "text-[#e7a877] border-[#e7a877]/40",
  CONFIG_PENDING: "text-[#e7a877] border-[#e7a877]/40", WAITING_ON_JARVIS: "text-[#e7a877] border-[#e7a877]/40",
  EXPIRED: "text-[#e88f6a] border-[#e88f6a]/40", MISSING: "text-[#e88f6a] border-[#e88f6a]/40",
  DEGRADED: "text-[#e88f6a] border-[#e88f6a]/40", FAIL_CLOSED: "text-[#e88f6a] border-[#e88f6a]/40",
  UNKNOWN: "text-[#e88f6a] border-[#e88f6a]/40", NEEDS_CLASSIFICATION: "text-[#e88f6a] border-[#e88f6a]/40",
  UNKNOWN_NEEDS_CLASSIFICATION: "text-[#e88f6a] border-[#e88f6a]/40",
};
const Badge = ({ text, testid }) => (
  <span className={`inline-block border px-2 py-0.5 text-[10px] uppercase tracking-wide2 ${STATUS_TONE[text] || "text-[var(--bone)] border-[rgba(180,139,63,0.45)]"}`} title={text} data-testid={testid}><MacText>{hz(text)}</MacText></span>
);
const Dash = () => <span className="text-[var(--muted)]">—</span>;

function Login({ onIn }) {
  const [email, setEmail] = useState(""); const [pw, setPw] = useState("");
  const [loading, setLoading] = useState(false); const [err, setErr] = useState(null);
  const submit = async (e) => {
    e.preventDefault(); setLoading(true); setErr(null);
    try { await adminLogin(email, pw); onIn(); }
    catch (e) { setErr(errMsg(e.response?.data?.detail, "Login failed.")); }
    finally { setLoading(false); }
  };
  return (
    <div className="min-h-screen bg-[var(--navy)] flex items-center justify-center px-4">
      <form onSubmit={submit} className="w-full max-w-sm" data-testid="gov-login-form">
        <h1 className="font-display uppercase tracking-wide2 text-[var(--gold)] text-xl mb-4">Founder Governance</h1>
        <input className={inputCls} type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="gov-login-email" aria-label="Email" />
        <input className={inputCls} type="password" placeholder="Password" value={pw} onChange={(e) => setPw(e.target.value)} data-testid="gov-login-password" aria-label="Password" />
        <button disabled={loading} className="jmh-cta mt-2 disabled:opacity-50" data-testid="gov-login-submit">{loading ? "…" : "Enter"}</button>
        {err && <p className="mt-3 text-sm text-[#e88f6a]" data-testid="gov-login-error">{err}</p>}
      </form>
    </div>
  );
}

const SectionTitle = ({ children }) => (
  <h2 className="font-display uppercase tracking-wide2 text-[12px] text-[var(--gold)]/80 mb-2 mt-6">{children}</h2>
);
const Th = ({ children }) => <th className="px-3 py-2 text-left">{children}</th>;

// ----------------------------------------------------------------- P7 Payroll Boundaries
function PayrollTab() {
  const [d, setD] = useState(null); const [err, setErr] = useState(null);
  useEffect(() => { govPayrollBoundaries().then(setD).catch((e) => setErr(errMsg(e.response?.data?.detail, "Unavailable."))); }, []);
  if (err) return <p className="text-sm text-[#e88f6a]" data-testid="payroll-error">{err}</p>;
  if (!d) return <p className="text-sm text-[var(--muted)]" data-testid="payroll-loading">Loading boundaries…</p>;
  return (
    <div data-testid="payroll-view">
      <p className="text-[11px] text-[var(--muted)] mb-3">{d.authority_note}</p>
      <div className="flex flex-wrap gap-2 mb-4" data-testid="payroll-summary">
        {Object.entries(d.by_boundary).filter(([, v]) => v > 0).map(([k, v]) => (
          <span key={k} className="border border-[rgba(180,139,63,0.25)] px-2 py-1 text-[11px]"><Badge text={k} /> <span className="ml-1 text-[var(--bone)]">{v}</span></span>
        ))}
        {d.total === 0 && <p className="text-sm text-[var(--muted)]" data-testid="payroll-empty">No authoritative records to classify yet.</p>}
      </div>
      {d.total > 0 && (
        <div className="overflow-x-auto"><table className="w-full text-sm border border-[rgba(180,139,63,0.18)]" data-testid="payroll-table">
          <thead><tr className="text-[var(--muted)] text-xs uppercase tracking-wide2"><Th>Boundary</Th><Th>Entity</Th><Th>Source</Th><Th>Date</Th><Th>State</Th></tr></thead>
          <tbody>
            {d.rows.slice(0, 100).map((r, i) => (
              <tr key={i} className="border-t border-[rgba(180,139,63,0.12)]" data-testid={`payroll-row-${i}`}>
                <td className="px-3 py-2"><Badge text={r.boundary_type} testid={`payroll-boundary-${i}`} /></td>
                <td className="px-3 py-2">{r.entity_name || <Dash />}</td>
                <td className="px-3 py-2 text-[var(--muted)]"><MacText>{hz(r.source_system)}</MacText></td>
                <td className="px-3 py-2">{r.effective_date ? String(r.effective_date).slice(0, 10) : <Dash />}</td>
                <td className="px-3 py-2">{r.needs_review ? <span className="text-[#e7a877] text-[11px]" data-testid={`payroll-review-${i}`}>NEEDS REVIEW</span> : <span className="text-[#8fd19e] text-[11px]">DERIVED</span>}</td>
              </tr>
            ))}
          </tbody>
        </table></div>
      )}
      {d.total > 100 && <p className="text-[11px] text-[var(--muted)] mt-2" data-testid="payroll-cap-note">Showing first 100 of {d.total} derived records.</p>}
    </div>
  );
}

// ----------------------------------------------------------------- generic register list + intake
function RegisterBlock({ title, testid, columns, rows, statusKey, statusOf, form, onSubmit }) {
  const [open, setOpen] = useState(false);
  const [vals, setVals] = useState({});
  const [busy, setBusy] = useState(false); const [err, setErr] = useState(null);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr(null);
    try { await onSubmit(vals); setVals({}); setOpen(false); }
    catch (e) { setErr(errMsg(e.response?.data?.detail, "Could not save.")); }
    finally { setBusy(false); }
  };
  return (
    <section className="mb-6" data-testid={`${testid}-block`}>
      <div className="flex items-center justify-between">
        <SectionTitle>{title} · {rows.length}</SectionTitle>
        <button onClick={() => setOpen((o) => !o)} className="font-display uppercase tracking-wide2 text-[11px] text-[var(--gold-lite)]" data-testid={`${testid}-add-toggle`}>{open ? "Close" : "+ Record"}</button>
      </div>
      {open && (
        <form onSubmit={submit} className="border border-[rgba(180,139,63,0.2)] p-3 mb-3" data-testid={`${testid}-form`}>
          {form.map((f) => (
            <input key={f.k} className={inputCls} placeholder={f.label + (f.req ? " *" : "")} value={vals[f.k] || ""} onChange={(e) => setVals((v) => ({ ...v, [f.k]: e.target.value }))} data-testid={`${testid}-field-${f.k}`} />
          ))}
          <button disabled={busy} className="jmh-cta mt-1 disabled:opacity-50" data-testid={`${testid}-submit`}>{busy ? "…" : "Save record"}</button>
          {err && <p className="mt-2 text-sm text-[#e88f6a]" data-testid={`${testid}-error`}>{err}</p>}
        </form>
      )}
      {rows.length === 0 ? <p className="text-sm text-[var(--muted)]" data-testid={`${testid}-empty`}>No records are available. Information without supporting evidence remains identified as missing or unknown.</p>
        : <div className="overflow-x-auto"><table className="w-full text-sm border border-[rgba(180,139,63,0.18)]" data-testid={`${testid}-table`}>
            <thead><tr className="text-[var(--muted)] text-xs uppercase tracking-wide2">{columns.map((c) => <Th key={c.k}>{c.label}</Th>)}<Th>Status</Th></tr></thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.id || i} className="border-t border-[rgba(180,139,63,0.12)]" data-testid={`${testid}-row-${i}`}>
                  {columns.map((c) => <td key={c.k} className="px-3 py-2">{r[c.k] ? (c.date ? String(r[c.k]).slice(0, 10) : String(r[c.k])) : <Dash />}</td>)}
                  <td className="px-3 py-2"><Badge text={statusOf ? statusOf(r) : r[statusKey]} testid={`${testid}-status-${i}`} /></td>
                </tr>
              ))}
            </tbody>
          </table></div>}
    </section>
  );
}

function InsuranceRiskDRTab() {
  const [ins, setIns] = useState([]); const [risk, setRisk] = useState([]); const [dr, setDr] = useState([]);
  const [err, setErr] = useState(null);
  const load = useCallback(() => {
    Promise.all([govListInsurance(), govListRisk(), govListDR()])
      .then(([a, b, c]) => { setIns(a.rows || []); setRisk(b.rows || []); setDr(c.rows || []); })
      .catch((e) => setErr(errMsg(e.response?.data?.detail, "Unavailable.")));
  }, []);
  useEffect(() => { load(); }, [load]);
  if (err) return <p className="text-sm text-[#e88f6a]" data-testid="p8-error">{err}</p>;
  return (
    <div data-testid="p8-view">
      <RegisterBlock title="Insurance" testid="insurance" rows={ins} statusKey="coverage_status"
        columns={[{ k: "policy_category", label: "Category" }, { k: "carrier", label: "Carrier" }, { k: "covered_entity_id", label: "Entity" }, { k: "expiration_date", label: "Expires", date: true }]}
        form={[{ k: "policy_category", label: "Policy category", req: true }, { k: "carrier", label: "Carrier" }, { k: "covered_entity_id", label: "Covered entity id" }, { k: "policy_reference", label: "Policy reference (redacted)" }, { k: "effective_date", label: "Effective date YYYY-MM-DD" }, { k: "expiration_date", label: "Expiration date YYYY-MM-DD" }, { k: "verification_state", label: "Verification (VERIFIED/UNVERIFIED)" }]}
        onSubmit={async (v) => { await govCreateInsurance(v); load(); }} />
      <RegisterBlock title="Risk" testid="risk" rows={risk} statusOf={(r) => (r.is_open ? "OPEN" : "CLOSED")}
        columns={[{ k: "risk_title", label: "Risk" }, { k: "category", label: "Category" }, { k: "affected_ref", label: "Affected" }, { k: "owner", label: "Owner" }, { k: "mitigation_status", label: "Mitigation" }]}
        form={[{ k: "risk_title", label: "Risk title", req: true }, { k: "category", label: "Category" }, { k: "affected_ref", label: "Affected entity/system" }, { k: "owner", label: "Owner" }, { k: "mitigation_status", label: "Mitigation status (OPEN/MITIGATED/RESOLVED)" }, { k: "dependency", label: "Dependency" }]}
        onSubmit={async (v) => { await govCreateRisk(v); load(); }} />
      <RegisterBlock title="Disaster Recovery / Continuity" testid="dr" rows={dr} statusKey="dr_status"
        columns={[{ k: "system_ref", label: "System" }, { k: "backup_dependency", label: "Backup" }, { k: "owner", label: "Owner" }, { k: "last_verified_date", label: "Last verified", date: true }]}
        form={[{ k: "system_ref", label: "System reference", req: true }, { k: "backup_dependency", label: "Backup dependency" }, { k: "continuity_dependency", label: "Continuity dependency" }, { k: "owner", label: "Owner" }, { k: "recovery_status", label: "Recovery status (VERIFIED/UNVERIFIED)" }, { k: "last_verified_date", label: "Last verified YYYY-MM-DD" }]}
        onSubmit={async (v) => { await govCreateDR(v); load(); }} />
    </div>
  );
}

function StewardshipTab() {
  const [rows, setRows] = useState([]); const [err, setErr] = useState(null); const [note, setNote] = useState("");
  const load = useCallback(() => {
    govListStewardship().then((d) => { setRows(d.rows || []); setNote(d.authority_note || ""); }).catch((e) => setErr(errMsg(e.response?.data?.detail, "Unavailable.")));
  }, []);
  useEffect(() => { load(); }, [load]);
  if (err) return <p className="text-sm text-[#e88f6a]" data-testid="p9-error">{err}</p>;
  return (
    <div data-testid="p9-view">
      <p className="text-[11px] text-[var(--muted)] mb-2">{note}</p>
      <RegisterBlock title="Stewardship Impact Record" testid="stewardship" rows={rows} statusKey="evidence_class"
        columns={[{ k: "initiative", label: "Initiative" }, { k: "entity_ref", label: "Entity" }, { k: "observed_outcome", label: "Observed outcome" }, { k: "time_period", label: "Period" }, { k: "source", label: "Source" }]}
        form={[{ k: "initiative", label: "Initiative", req: true }, { k: "entity_ref", label: "Entity ref" }, { k: "stated_objective", label: "Stated objective" }, { k: "observed_outcome", label: "Observed outcome" }, { k: "evidence_class", label: "Evidence class (RECORDED_FACT/SOURCE_CLAIM/DERIVED_SUMMARY)" }, { k: "time_period", label: "Time period" }, { k: "source", label: "Source" }, { k: "qualitative_evidence", label: "Qualitative evidence" }]}
        onSubmit={async (v) => { await govCreateStewardship(v); load(); }} />
    </div>
  );
}

function MeshHealthTab() {
  const [d, setD] = useState(null); const [err, setErr] = useState(null);
  useEffect(() => { govMeshHealth().then(setD).catch((e) => setErr(errMsg(e.response?.data?.detail, "Unavailable."))); }, []);
  if (err) return <p className="text-sm text-[#e88f6a]" data-testid="mesh-error">{err}</p>;
  if (!d) return <p className="text-sm text-[var(--muted)]" data-testid="mesh-loading">Loading mesh health…</p>;
  const Row = ({ label, children, testid }) => (
    <div className="flex items-start justify-between gap-4 border-b border-[rgba(180,139,63,0.12)] py-2">
      <span className="text-[var(--muted)] text-xs uppercase tracking-wide2">{label}</span>
      <span className="text-sm text-right" data-testid={testid}>{children}</span>
    </div>
  );
  const a = d.jarvis_authority || {};
  return (
    <div data-testid="mesh-view" className="max-w-2xl">
      <div className="flex items-center gap-3 mb-4">
        <Badge text={d.overall_health} testid="mesh-overall" />
        <Badge text={d.environment_label} testid="mesh-environment" />
        <span className="text-[11px] text-[var(--muted)]" data-testid="mesh-identity">{d.jmh_identity} · {d.contract} v{d.contract_version}</span>
      </div>
      <Row label={<><MacBrand /> authority</>} testid="mesh-authority">{hz(a.state)}{a.reason ? ` · ${a.reason}` : ""}</Row>
      <Row label="Growth base" testid="mesh-base">{a.base_url || <Dash />}</Row>
      <Row label="Siblings in mesh" testid="mesh-siblings">{a.sibling_count ?? <Dash />}</Row>
      <Row label="Work Bus configured" testid="mesh-workbus">{d.work_bus_configured ? "Yes" : "No"}</Row>
      <Row label="Outbound emitter" testid="mesh-outbound">{d.outbound_emitter_configured ? "Configured" : "Not configured"}</Row>
      <Row label="Recovery-pull" testid="mesh-pull">{d.recovery_pull_configured ? "Configured" : "Not configured"}</Row>
      <Row label="Registration" testid="mesh-registration">{hz(d.registration_state)}</Row>
      <Row label="Receiver" testid="mesh-receiver">{d.receiver?.present ? "Present" : "Absent"} · {d.receiver?.auth}</Row>
      <Row label="Pending inbound" testid="mesh-pending">{d.pending_inbound_items}</Row>
      <Row label="Last received" testid="mesh-last-received">{d.last_received_at ? String(d.last_received_at).slice(0, 19).replace("T", " ") : <Dash />}</Row>
      <Row label="Last verified" testid="mesh-last-verified">{String(d.last_verified_at).slice(0, 19).replace("T", " ")}</Row>
      <p className="text-[11px] text-[var(--muted)] mt-3">{d.truth_note}</p>
    </div>
  );
}

const TABS = [
  { k: "payroll", label: "Payroll Boundaries", C: PayrollTab },
  { k: "p8", label: "Insurance / Risk / DR", C: InsuranceRiskDRTab },
  { k: "stewardship", label: "Stewardship", C: StewardshipTab },
  { k: "mesh", label: "Mesh Health", C: MeshHealthTab },
];

function Console({ onSignOut }) {
  const [tab, setTab] = useState("payroll");
  const [health, setHealth] = useState(null);
  useEffect(() => { govMeshHealth().then(setHealth).catch(() => {}); }, []);
  const Active = TABS.find((t) => t.k === tab).C;
  return (
    <div className="min-h-screen bg-[var(--navy)] text-[var(--bone)]">
      <header className="border-b border-[rgba(180,139,63,0.2)] px-4 md:px-8 py-4 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3 flex-wrap">
          <h1 className="font-display uppercase tracking-wide2 text-[var(--gold)] text-lg">Founder Governance</h1>
          {health && (
            <span className="flex items-center gap-1.5" data-testid="header-mesh-badge" title={`${health.overall_health} · ${health.environment_label}`}>
              <Badge text={health.overall_health} />
              <Badge text={health.environment_label} />
            </span>
          )}
        </div>
        <button onClick={onSignOut} className="font-display uppercase tracking-wide2 text-xs text-[var(--gold-lite)]" data-testid="gov-signout">Sign out</button>
      </header>
      <nav className="px-4 md:px-8 pt-4 flex gap-2 flex-wrap border-b border-[rgba(180,139,63,0.12)]" data-testid="gov-tabs">
        {TABS.map((t) => (
          <button key={t.k} onClick={() => setTab(t.k)} data-testid={`gov-tab-${t.k}`}
            className={`font-display uppercase tracking-wide2 text-[11px] px-3 py-2 border-b-2 -mb-px transition-colors ${tab === t.k ? "border-[var(--gold)] text-[var(--gold)]" : "border-transparent text-[var(--muted)] hover:text-[var(--bone)]"}`}>
            {t.label}
          </button>
        ))}
      </nav>
      <main className="px-4 md:px-8 py-6 max-w-6xl">
        <Active />
      </main>
    </div>
  );
}

export default function FounderGovernance() {
  const [authed, setAuthed] = useState(!!getToken());
  if (!authed) return <Login onIn={() => setAuthed(true)} />;
  return <Console onSignOut={() => { clearToken(); setAuthed(false); }} />;
}
